import React from "react";
import type { FormEvent } from "react";
import { useState } from "react";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import logo from "../assets/mb_icon.png";

interface LoginPageProps {
  onLogin: () => void;
}

const firebaseConfig = {
  apiKey: "AIzaSyDzHm656F7pPQgC3N_33kZYlbMy84TZR1E",
  authDomain: "https://accounts.google.com/o/oauth2/auth",
  databaseURL: "https://smart-school-kzbgn7.firebaseio.com",
  projectId: "smart-school-kzbgn7",
  storageBucket: "smart-school-kzbgn7.firebasestorage.app", 
  messagingSenderId: "107435169212721791618",
  appId: "1:1035938954820:android:6fb9004fa61651ab538611"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [userUID, setUserUID] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    console.log('🔐 Starting login process...');
    console.log('📝 User ID entered:', userUID);

    try {
      // Step 1: Fetch user document from /users/{userUID}
      const userDocRef = doc(db, "users", userUID);
      console.log('📍 Fetching user document from path:', `users/${userUID}`);

      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ User document found:', userData);
        console.log('🏢 Domain from document:', userData.domain);

        // Ensure domain field exists
        if (!userData.domain) {
          console.error('❌ Domain field is missing in user document!');
          setError('User document is missing domain field. Please contact administrator.');
          setLoading(false);
          return;
        }

        // Step 2: Fetch schoolName from tenant-domains
        const tenantDocRef = doc(db, "tenant-domains", userData.domain);
        console.log('📍 Fetching tenant document from path:', `tenant-domains/${userData.domain}`);

        let schoolName = userData.domain; // Default to domain if tenant not found
        try {
          const tenantDoc = await getDoc(tenantDocRef);
          if (tenantDoc.exists()) {
            const tenantData = tenantDoc.data();
            schoolName = tenantData.schoolName || userData.domain;
            console.log('✅ Tenant document found, schoolName:', schoolName);
          } else {
            console.warn('⚠️ Tenant document not found, using domain as schoolName');
          }
        } catch (tenantError) {
          console.warn('⚠️ Error fetching tenant document:', tenantError);
        }

        // Step 3: Authenticate with Firebase Auth
        console.log('🔑 Authenticating with email:', userData.email);
        const userCredential = await signInWithEmailAndPassword(auth, userData.email, password);
        const user = userCredential.user;

        console.log('✅ Authentication successful!');
        console.log('👤 Firebase Auth UID:', user.uid);

        // Step 4: Store user data in localStorage with schoolName
        const isSuperUser = userData.superAdmin === true;
        const userDataWithSchool = {
          ...userData,
          schoolName: schoolName,
          isSuperUser: isSuperUser
        };

        localStorage.setItem('userUID', user.uid);
        localStorage.setItem('userData', JSON.stringify(userDataWithSchool));

        // For super users, set their own domain as default active domain
        if (isSuperUser) {
          // Use first allowed domain or user's own domain as default
          const allowedDomains: string[] = userData.domains || [];
          const defaultDomain = allowedDomains.length > 0 ? allowedDomains[0] : userData.domain;
          localStorage.setItem('activeDomain', defaultDomain);
          console.log('🔑 Super user detected! Allowed domains:', allowedDomains);
        } else {
          localStorage.removeItem('activeDomain');
        }

        console.log('💾 Stored in localStorage:');
        console.log('  - userUID:', user.uid);
        console.log('  - userData:', userDataWithSchool);
        console.log('  - domain:', userData.domain);
        console.log('  - schoolName:', schoolName);
        console.log('  - isSuperUser:', isSuperUser);
        console.log('  - allowedDomains:', userData.domains || []);

        console.log('🎉 Login complete! Redirecting...');
        onLogin();
      } else {
        console.error('❌ User document not found at path:', `users/${userUID}`);
        setError(`User ID "${userUID}" not found. Please check your credentials or contact administrator.`);
      }
    } catch (error: any) {
      console.error('❌ Authentication error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'permission-denied') {
        setError('Permission denied. Please check Firestore security rules.');
      } else {
        setError(`Failed to authenticate: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        {/* Logo positioned separately */}
        <div className="centered-container">
          <img
            src={logo}
            alt="Smart School Logo"
            style={{
              width: 300,
              filter: 'drop-shadow(0 0 10px rgba(231, 237, 239, 0.5))',
              border: '0',
              outline: 'none',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              opacity: 0.9
            }}
          />
        </div>

        {/* Login Box */}
        <div className="shadow bg-card rounded-16 p-4" style={{ maxWidth: 400, width: "100%", opacity: 0.9 }}>
          <div className="text-center mb-4">
            <div className="fs-2 fw-bold" style={{ color: "var(--secondary)" }}>
              Welcome Back
            </div>
            <div className="fs-5" style={{ color: "var(--text-on-primary)", opacity: 0.8 }}>
              Sign in to continue
            </div>
          </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label text-white">User ID</label>
            <input
              type="text"
              className="form-control input-custom"
              placeholder="Enter your User ID"
              value={userUID}
              onChange={e => setUserUID(e.target.value.trim())}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label text-white">Password</label>
            <input
              type="password"
              className="form-control input-custom"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <input type="checkbox" id="remember" />
              <label htmlFor="remember" className="text-white ms-2">
                Remember me
              </label>
            </div>
            <a 
              href="#" 
              className="text-secondary" 
              style={{ textDecoration: "underline" }}
            >
              Forgot Password?
            </a>
          </div>

          <button
            className="btn btn-theme w-100 fw-bold py-2 mb-3"
            type="submit"
            disabled={loading || !userUID.trim() || !password.trim()}
          >
            {loading ? "Authenticating..." : "Login"}
          </button>

          <div className="text-center" style={{ color: 'var(--text-on-primary)', opacity: 0.85 }}>
            or
          </div>

          <div className="d-flex justify-content-center gap-2 mt-3">
            <button type="button" className="btn btn-light border">
              <i className="bi bi-google"></i>
            </button>
            <button type="button" className="btn btn-light border">
              <i className="bi bi-apple"></i>
            </button>
            <button type="button" className="btn btn-light border">
              <i className="bi bi-microsoft"></i>
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage;